import type { ReactNode } from "react";

import { Icon, type IconName } from "./icon";
import { StatusBadge, type StatusTone } from "./status-badge";

/** A tone and an icon that a domain keeps for one of its states (`stateLook`, `priorityLook`). */
export type Look = Readonly<{ tone: StatusTone; icon: IconName }>;

/** A `StatusBadge` drawn from a `Look`: the icon and the word together, never colour alone. */
export function StatusLook({ look, children }: Readonly<{ look: Look; children: ReactNode }>) {
  return (
    <StatusBadge tone={look.tone} glyph={<Icon name={look.icon} />}>
      {children}
    </StatusBadge>
  );
}

/** The look of a code that no table knows (a state of a newer server, an open record): a neutral tone and a question mark. */
const UNKNOWN_LOOK: Look = { tone: "neutral", icon: "help-circle" };

/**
 * The look that a table keeps for a code. A code that is not in the table gets `UNKNOWN_LOOK`, so a screen never reads `undefined.icon`.
 * The table is keyed by a closed type, but a record that the API sends open (a request detail) can carry a state that this build does
 * not know.
 */
export function lookOf(
  table: Readonly<Record<string, Look>>,
  code: string | null | undefined,
): Look {
  return code !== null && code !== undefined && Object.hasOwn(table, code)
    ? (table[code] as Look)
    : UNKNOWN_LOOK;
}
